// Unified filter panel for the Leads list.
// A single "Filters" button opens a dropdown panel; each section
// (Owner, Source, Labels, Date) is independently collapsible.

import React, { useState, useRef, useEffect, useMemo } from "react";
import { Filter, X, Calendar, User, Tag, Globe, ChevronDown, ChevronRight, Check, Search } from "lucide-react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { MemberPickerPanel } from "./MemberPicker";
import { useTeamMembers, formatMember, useMyCrmRole } from "../hooks/useTeamMembers";
import { useLeadFilterOptions } from "../hooks/useLeads";

const { RangePicker } = DatePicker;

// ── helpers ────────────────────────────────────────────────────────────────

function useOutsideClick(ref, onClose, enabled) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      // Ant Design calendar/picker popups are rendered in portals outside our
      // panel div. Don't close if the click landed inside any of those overlays.
      if (e.target.closest(".ant-picker-dropdown, .ant-picker-panel-container, .ant-picker-panel")) return;
      onClose();
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [enabled, onClose]);
}

// ── Section wrapper (collapsible) ──────────────────────────────────────────

function Section({ title, icon: Icon, activeCount = 0, open, onToggle, onClear, children }) {
  return (
    <div className="tg-filter-section">
      <div style={{ display: "flex", alignItems: "center" }}>
        <button
          type="button"
          onClick={onToggle}
          className="tg-filter-head"
          aria-expanded={open}
          style={{
            flex: 1, display: "flex", alignItems: "center", gap: 8,
            padding: "9px 12px", border: "none",
            cursor: "pointer", color: "var(--color-text-primary)", fontSize: 13, fontWeight: 500,
          }}
        >
          {Icon && <Icon size={13} style={{ color: "var(--color-text-secondary)", flexShrink: 0 }} />}
          <span style={{ flex: 1, textAlign: "left" }}>{title}</span>
          {activeCount > 0 && (
            <span style={{
              minWidth: 18, height: 18, borderRadius: 999, padding: "0 5px",
              background: "var(--crm-primary)", color: "#fff",
              fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}>{activeCount}</span>
          )}
          {open
            ? <ChevronDown size={13} style={{ color: "var(--color-text-secondary)", flexShrink: 0 }} />
            : <ChevronRight size={13} style={{ color: "var(--color-text-secondary)", flexShrink: 0 }} />}
        </button>
        {activeCount > 0 && onClear && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            title="Clear this filter"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--color-text-secondary)", padding: "9px 10px 9px 4px",
              display: "inline-flex", alignItems: "center",
            }}
          >
            <X size={13} />
          </button>
        )}
      </div>
      {open && (
        <div style={{ padding: "4px 12px 10px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Owner multi-select section content ────────────────────────────────────

function OwnerSection({ value = [], onChange }) {
  return (
    <MemberPickerPanel
      values={value}
      onChange={(id) => {
        if (!id) return;
        const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
        onChange(next.length ? next : undefined);
      }}
      allowClear={false}
      compact
    />
  );
}

// ── Source multi-select section content ──────────────────────────────────

function SourceSection({ value = [], onChange }) {
  const { data } = useLeadFilterOptions();
  const sources = data?.sources || [];
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? sources.filter((s) => s.toLowerCase().includes(n)) : sources;
  }, [sources, q]);
  const toggle = (s) => {
    const next = value.includes(s) ? value.filter((v) => v !== s) : [...value, s];
    onChange(next.length ? next : undefined);
  };

  return (
    <div>
      <div style={{ position: "relative", marginBottom: 6 }}>
        <Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search sources"
          style={{
            width: "100%", height: 28, padding: "0 8px 0 26px", border: "1px solid var(--color-border-subtle)",
            borderRadius: 6, fontSize: 12, background: "var(--color-bg-base)",
            color: "var(--color-text-primary)", outline: "none", boxSizing: "border-box",
          }}
        />
      </div>
      <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
        {filtered.map((s) => {
          const sel = value.includes(s);
          return (
            <button key={s} type="button" onClick={() => toggle(s)} className="tg-filter-opt" style={optRow(sel)}>
              <span style={checkbox(sel, true)}>{sel && <Check size={9} />}</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s}</span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "6px 4px" }}>No sources yet</div>
        )}
      </div>
    </div>
  );
}

// ── Labels multi-select section content ──────────────────────────────────

function LabelsSection({ value = [], onChange }) {
  const { data } = useLeadFilterOptions();
  const labels = data?.labels || [];
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? labels.filter((l) => l.toLowerCase().includes(n)) : labels;
  }, [labels, q]);
  const toggle = (l) => {
    const next = value.includes(l) ? value.filter((v) => v !== l) : [...value, l];
    onChange(next.length ? next : undefined);
  };

  return (
    <div>
      <div style={{ position: "relative", marginBottom: 6 }}>
        <Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search labels"
          style={{
            width: "100%", height: 28, padding: "0 8px 0 26px", border: "1px solid var(--color-border-subtle)",
            borderRadius: 6, fontSize: 12, background: "var(--color-bg-base)",
            color: "var(--color-text-primary)", outline: "none", boxSizing: "border-box",
          }}
        />
      </div>
      <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
        {filtered.map((l) => {
          const sel = value.includes(l);
          return (
            <button key={l} type="button" onClick={() => toggle(l)} className="tg-filter-opt" style={optRow(sel)}>
              <span style={checkbox(sel, true)}>{sel && <Check size={9} />}</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l}</span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "6px 4px" }}>No labels yet</div>
        )}
      </div>
    </div>
  );
}

// ── Date range section content ────────────────────────────────────────────

function DateSection({ from, to, onChange }) {
  const value = (from || to) ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null;
  return (
    <RangePicker
      value={value}
      onChange={(dates) => {
        onChange({
          from: dates?.[0] ? dates[0].format("YYYY-MM-DD") : undefined,
          to: dates?.[1] ? dates[1].format("YYYY-MM-DD") : undefined,
        });
      }}
      format="DD/MM/YY"
      placeholder={["Start", "End"]}
      allowEmpty={[true, true]}
      suffixIcon={<Calendar size={12} />}
      className="crm-filter-picker"
      style={{ width: "100%", borderRadius: 6, fontSize: 12 }}
      popupStyle={{ zIndex: 99999 }}
    />
  );
}

// ── shared micro-styles ───────────────────────────────────────────────────

// Selected rows tint inline; unselected ones leave `background` unset so the
// .tg-filter-opt:hover rule can win.
const optRow = (sel) => ({
  display: "flex", alignItems: "center", gap: 8,
  padding: "6px 8px", border: "none",
  ...(sel ? { background: "var(--crm-primary-soft)" } : null),
  cursor: "pointer", borderRadius: 6, textAlign: "left",
  fontSize: 13, color: "var(--color-text-primary)", width: "100%",
});

const checkbox = (sel, multi) => ({
  width: 14, height: 14, borderRadius: multi ? 3 : 999, flexShrink: 0,
  border: `1.5px solid ${sel ? "var(--crm-primary)" : "#94a3b8"}`,
  background: sel ? "var(--crm-primary)" : "transparent",
  color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center",
});

// ── Main export ───────────────────────────────────────────────────────────

export default function LeadsFilters({ filters, onChange }) {
  const [open, setOpen] = useState(false);
  // Everything collapsed on open: the panel should read as a short list of the
  // facets you can filter by, not land you inside one of them.
  const [openSection, setOpenSection] = useState(null);
  const panelRef = useRef(null);
  const { isOwner } = useMyCrmRole();
  const { byId } = useTeamMembers();

  const toggleSection = (key) => setOpenSection((cur) => (cur === key ? null : key));

  useOutsideClick(panelRef, () => setOpen(false), open);

  const set = (k, v) => onChange({ ...filters, [k]: v });
  const setDate = ({ from, to }) => onChange({ ...filters, updatedFrom: from, updatedTo: to });

  const ownerCount = isOwner ? (filters.ownerIds?.length || 0) : 0;
  const sourceCount = filters.sources?.length || 0;
  const labelsCount = filters.labels?.length || 0;
  const dateCount = (filters.updatedFrom || filters.updatedTo) ? 1 : 0;
  const totalActive = ownerCount + sourceCount + labelsCount + dateCount;

  // Active filter summary pills — each with its own clear action
  const summaryPills = [];
  if (ownerCount > 0) {
    const label = ownerCount === 1
      ? (formatMember(byId.get(String(filters.ownerIds[0]))) || "1 owner")
      : `${ownerCount} owners`;
    summaryPills.push({ key: "owners", label, onClear: () => set("ownerIds", undefined) });
  }
  if (sourceCount === 1) summaryPills.push({ key: "source", label: filters.sources[0], onClear: () => set("sources", undefined) });
  else if (sourceCount > 1) summaryPills.push({ key: "source", label: `${sourceCount} sources`, onClear: () => set("sources", undefined) });
  if (labelsCount === 1) summaryPills.push({ key: "labels", label: filters.labels[0], onClear: () => set("labels", undefined) });
  else if (labelsCount > 1) summaryPills.push({ key: "labels", label: `${labelsCount} labels`, onClear: () => set("labels", undefined) });
  if (filters.updatedFrom || filters.updatedTo) summaryPills.push({ key: "date", label: "created", onClear: () => onChange({ ...filters, updatedFrom: undefined, updatedTo: undefined }) });

  return (
    <div ref={panelRef} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`tg-tool ${open ? "is-open" : ""}`}
        style={totalActive > 0 ? { background: "var(--crm-primary-soft)", color: "var(--crm-primary)" } : undefined}
      >
        <Filter size={13} />
        Filters
        {totalActive > 0 && (
          <span style={{
            minWidth: 18, height: 18, borderRadius: 999, padding: "0 5px",
            background: "var(--crm-primary)", color: "#fff",
            fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>{totalActive}</span>
        )}
        <ChevronDown size={11} style={{ opacity: 0.6 }} />
      </button>

      {/* Active summary pills — each has its own × to clear just that filter */}
      {summaryPills.map(({ key, label, onClear }) => (
        <span key={key} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          fontSize: 12, padding: "3px 6px 3px 9px", borderRadius: 6,
          background: "var(--crm-primary-soft)", color: "var(--crm-primary)", fontWeight: 500,
        }}>
          {label}
          <button
            type="button"
            onClick={onClear}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--crm-primary)", display: "inline-flex", alignItems: "center", padding: 0,
            }}
          >
            <X size={11} />
          </button>
        </span>
      ))}
      {totalActive > 0 && (
        <button
          type="button"
          onClick={() => onChange({})}
          style={{
            background: "transparent", border: "none",
            color: "var(--color-text-secondary)", fontSize: 12, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 3, padding: "0 2px",
          }}
        >
          <X size={11} /> Clear
        </button>
      )}

      {/* Dropdown panel */}
      {open && (
        // Right-anchored: this button lives at the right end of the toolbar, so
        // a left-anchored panel hung off the edge of the viewport and clipped
        // its own close button and search fields.
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 9999,
          background: "var(--color-bg-surface)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: 10, boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
          width: 300, overflow: "hidden",
          display: "flex", flexDirection: "column",
          maxHeight: "min(70vh, 540px)",
        }}>
          {/* Panel header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 12px", borderBottom: "1px solid var(--color-border-subtle)",
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>Filters</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {totalActive > 0 && (
                <button
                  type="button"
                  onClick={() => { onChange({}); }}
                  style={{ background: "transparent", border: "none", fontSize: 12, color: "var(--crm-primary)", cursor: "pointer", padding: 0 }}
                >
                  Clear all
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--color-text-secondary)", display: "inline-flex", padding: 2 }}
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Sections — scroll as a group so the header and its Clear all stay
              reachable no matter how tall the expanded facet is. */}
          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {isOwner && (
            <Section title="Owner" icon={User} activeCount={ownerCount}
              open={openSection === "owner"} onToggle={() => toggleSection("owner")}
              onClear={() => set("ownerIds", undefined)}
            >
              <OwnerSection
                value={filters.ownerIds || []}
                onChange={(v) => set("ownerIds", v)}
              />
            </Section>
          )}
          <Section title="Source" icon={Globe} activeCount={sourceCount}
            open={openSection === "source"} onToggle={() => toggleSection("source")}
            onClear={() => set("sources", undefined)}
          >
            <SourceSection value={filters.sources || []} onChange={(v) => set("sources", v)} />
          </Section>
          <Section title="Labels" icon={Tag} activeCount={labelsCount}
            open={openSection === "labels"} onToggle={() => toggleSection("labels")}
            onClear={() => set("labels", undefined)}
          >
            <LabelsSection value={filters.labels || []} onChange={(v) => set("labels", v)} />
          </Section>
          <Section title="Created" icon={Calendar} activeCount={dateCount}
            open={openSection === "date"} onToggle={() => toggleSection("date")}
            onClear={() => onChange({ ...filters, updatedFrom: undefined, updatedTo: undefined })}
          >
            <DateSection from={filters.updatedFrom} to={filters.updatedTo} onChange={setDate} />
          </Section>
          </div>
        </div>
      )}
    </div>
  );
}
